import React, { useCallback, useState } from 'react';

import { HooksDisplayGrid } from 'ui-components/components/hooks-display-grid';

import { createBaseColumns } from './columns';
import { baseColumnsDefaultConfigs, columnIdToOrderByMap } from './columns/column-config';
import { PurchaseOrdersGridPlaceholder as EmptyGridPlaceholder } from './grid-placeholder';
import ErrorBoundary from '../../../../components/error-boundary';
import GridLoading from '../../../../components/grid-loading';
import { emptyArray } from 'ui-components/default-props/empty';
import { usePurchaseOrdersGridContext } from '../purchase-orders-grid-context';
import { buildColumns, getColumnId } from '../../../../utils/grid-column-utils';

/**
 * Purchase Order Grid component
 * Displays a grid of purchase orders fetched from the API.
 * Uses HooksDisplayGrid to render the data with the defined columns.
 * Shows a placeholder when no data is available.
 * @param {object} props - Component props
 * @param {Array<import('../api/purchase-order-api').PurchaseOrderRow>} props.data - The purchase orders data to display in the grid
 * @param {boolean} props.isLoading - Flag indicating if data is currently being loaded
 * @param {Array<string>} [props.selected] - Array of selected purchase order IDs
 * @param {(selectedIds: Array<string>) => void} [props.onSelectedChanged] - Callback for selection changes
 * @param {()=>Promise<void>} [props.refreshPurchaseOrders] - Callback for selection changes
 * @returns {JSX.Element} The rendered Purchase Order Grid component
 */
export const PurchaseOrderGrid = ({ selected = emptyArray, onSelectedChanged, data, isLoading, refreshPurchaseOrders }) => {
  const [gridColumns, setGridColumns] = useState(buildColumns(baseColumnsDefaultConfigs, createBaseColumns(refreshPurchaseOrders)));
  const { onSortChanged } = usePurchaseOrdersGridContext();

  const onColumnsChanged = useCallback(
    (columns) => {
      setGridColumns(buildColumns(columns, createBaseColumns(refreshPurchaseOrders)));
    },
    [refreshPurchaseOrders],
  );

  const onOrderByChanged = useCallback(
    (columns) => {
      setGridColumns(buildColumns(columns, createBaseColumns(refreshPurchaseOrders)));

      const sortedColumn = columns.find((col) => col.sorted);
      if (sortedColumn && columnIdToOrderByMap[sortedColumn.id]) {
        const orderBy = columnIdToOrderByMap[sortedColumn.id];
        const direction = sortedColumn.sorted === 'ascending' ? 'Ascending' : 'Descending';
        onSortChanged({ orderBy, direction });
      } else {
        onSortChanged({ orderBy: null, direction: null });
      }
    },
    [refreshPurchaseOrders, onSortChanged],
  );

  return (
    <ErrorBoundary errorMessageId='purchaseOrdersGrid'>
      <HooksDisplayGrid
        columns={gridColumns}
        data={!isLoading ? data : emptyArray}
        getColumnId={getColumnId}
        onColumnsChanged={onColumnsChanged}
        onOrderByChanged={onOrderByChanged}
        onSelectedChanged={onSelectedChanged}
        placeholder={isLoading ? <GridLoading /> : <EmptyGridPlaceholder />}
        selected={selected}
      />
    </ErrorBoundary>
  );
};
